'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormInputField } from '@/components/forms/form-input-field';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ItemsEditor, type ProjectItem } from '@/components/projects/ItemsEditor';
import { ContactsEditor, type ProjectContactDraft } from '@/components/projects/ContactsEditor';
import { ProjectBannerField } from '@/components/projects/ProjectBannerField';
import {
  ProjectDocumentsEditor,
  type ProjectDocumentDraft,
} from '@/components/projects/ProjectDocumentsEditor';
import { RotaryFocusAreasMultiSelect } from '@/components/projects/RotaryFocusAreasMultiSelect';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { formatIntegerInput } from '@/lib/integer-input';
import { Input } from '@/components/ui/input';
import {
  buildProjectContactsPayload,
  buildProjectCorePayload,
  buildProjectDocumentsPayload,
  buildProjectItemsPayload,
  validateIncompleteContacts,
} from '@/lib/project-form-payload';
import type { ProjectContact, ProjectDocument } from '@/types';

const projectFormSchema = z.object({
  name: z.string().min(1, 'Project name is required').max(200),
  description: z.string().optional(),
  location: z.string().optional(),
  happeningFor: z.string().optional(),
  hostClub: z.string().max(200).optional(),
  needAssessment: z.string().optional(),
  estimatedBeneficiaries: z.string().optional(),
  expectedCost: z.string().optional(),
});

type ProjectFormValues = z.infer<typeof projectFormSchema>;

export default function EditProjectPage() {
  const { communityId, projectId } = useParams<{ communityId: string; projectId: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<ProjectItem[]>([]);
  const [contacts, setContacts] = useState<ProjectContactDraft[]>([]);
  const [rotaryFocusAreaKeys, setRotaryFocusAreaKeys] = useState<string[]>([]);
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [documents, setDocuments] = useState<ProjectDocumentDraft[]>([]);
  const [isOpenForVendors, setIsOpenForVendors] = useState(false);

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: '',
      description: '',
      location: '',
      happeningFor: '',
      hostClub: '',
      needAssessment: '',
      estimatedBeneficiaries: '',
      expectedCost: '',
    },
  });

  useEffect(() => {
    async function loadProject() {
      if (!communityId || !projectId) return;

      try {
        const res = await axiosInstance.get(
          `/api/communities/${communityId}/projects/${projectId}`
        );
        const project = res.data;

        form.reset({
          name: project.name || '',
          description: project.description || '',
          location: project.location || '',
          happeningFor: project.happeningFor || '',
          hostClub: project.hostClub || '',
          needAssessment: project.needAssessment || '',
          estimatedBeneficiaries: project.estimatedBeneficiaries?.toString() || '',
          expectedCost: project.expectedCost
            ? formatIntegerInput(String(project.expectedCost))
            : '',
        });

        if (project.items && project.items.length > 0) {
          setItems(
            project.items.map((item: ProjectItem) => ({
              id: item.id,
              serialNumber: item.serialNumber,
              name: item.name,
              description: item.description || '',
              quantity: item.quantity,
              unit: item.unit || '',
            }))
          );
        } else {
          setItems([]);
        }

        if (project.contacts && project.contacts.length > 0) {
          setContacts(
            project.contacts.map((c: ProjectContact) => ({
              id: c.id,
              name: c.name ?? '',
              designation: c.designation ?? '',
              phone: c.phone ?? '',
              email: c.email ?? '',
              phoneOnWhatsApp: Boolean(c.phoneOnWhatsApp),
            }))
          );
        } else {
          setContacts([]);
        }

        setRotaryFocusAreaKeys(
          Array.isArray(project.rotaryFocusAreaKeys) ? [...project.rotaryFocusAreaKeys] : []
        );
        setBannerImageUrl(
          typeof project.bannerImageUrl === 'string' ? project.bannerImageUrl.trim() : ''
        );
        if (project.documents && project.documents.length > 0) {
          setDocuments(
            project.documents.map((d: ProjectDocument) => ({
              id: d.id,
              name: d.name,
              url: d.url,
              mimeType: d.mimeType || 'application/octet-stream',
              size: d.size ?? 0,
            }))
          );
        } else {
          setDocuments([]);
        }

        setIsOpenForVendors(project.isOpenForVendors || false);
      } catch (error) {
        console.error('Failed to load project', error);
        toast.error('Failed to load project');
        navigate(`/${communityId}/projects`);
      } finally {
        setIsLoading(false);
      }
    }

    loadProject();
  }, [communityId, projectId, form, navigate]);

  const onSubmit = async (values: ProjectFormValues) => {
    if (!communityId || !projectId) return;

    const validItems = buildProjectItemsPayload(items);

    const contactError = validateIncompleteContacts(contacts);
    if (contactError) {
      toast.error(contactError);
      return;
    }

    if (isOpenForVendors && validItems.length === 0) {
      toast.error('Project must have at least one item when open for vendors');
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}/projects/${projectId}`, {
        ...buildProjectCorePayload(values, {
          bannerImageUrl,
          rotaryFocusAreaKeys,
          includeEmptyBanner: true,
          alwaysIncludeRotaryKeys: true,
          alwaysIncludeHostClub: true,
        }),
        items: validItems,
        contacts: buildProjectContactsPayload(contacts),
        documents: buildProjectDocumentsPayload(documents),
      });
      toast.success('Project updated successfully!');
      navigate(`/${communityId}/projects/${projectId}`, { replace: true });
    } catch (error) {
      console.error('Failed to update project', error);
      toast.error('Failed to update project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${communityId}/projects/${projectId}`, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const validItemCount = items.filter((item) => item.name.trim() !== '').length;

  return (
    <div className="w-full max-w-5xl mx-auto px-0 md:px-4">
      <Card className="border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-xl md:text-2xl font-black tracking-tight text-foreground">
            Edit Project
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Update your community project details.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6 pb-8 overflow-x-auto">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 min-w-[280px]">
              <FormInputField
                control={form.control}
                name="name"
                label="Project Name"
                placeholder="e.g., Community Garden Initiative"
                required
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <div className="min-h-[150px]">
                        <RichTextEditor value={field.value || ''} onChange={field.onChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormInputField
                control={form.control}
                name="location"
                label="Location"
                placeholder="e.g., Community Center Park"
              />

              <FormInputField
                control={form.control}
                name="happeningFor"
                label="Happening For"
                placeholder="e.g., Youth development, Senior citizens"
              />

              <FormInputField
                control={form.control}
                name="hostClub"
                label="Host Club (optional)"
                placeholder="e.g., Rotary Club of Downtown"
              />

              <FormField
                control={form.control}
                name="needAssessment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Need Assessment</FormLabel>
                    <FormControl>
                      <div className="min-h-[150px]">
                        <RichTextEditor value={field.value || ''} onChange={field.onChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInputField
                  control={form.control}
                  name="estimatedBeneficiaries"
                  label="Estimated Beneficiaries"
                  placeholder="e.g., 100"
                  type="number"
                />

                <FormField
                  control={form.control}
                  name="expectedCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expected Cost (₹)</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          placeholder="e.g., 50,000"
                          value={field.value || ''}
                          onChange={(event) =>
                            field.onChange(formatIntegerInput(event.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {isOpenForVendors && validItemCount === 0 && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <p className="text-sm">
                    This project is open for vendors. You must add at least one item before saving.
                  </p>
                </div>
              )}

              <div className="border-t border-border pt-6 space-y-8">
                <RotaryFocusAreasMultiSelect
                  selectedIds={rotaryFocusAreaKeys}
                  onChange={setRotaryFocusAreaKeys}
                  disabled={isSubmitting}
                />
                <ProjectBannerField
                  bannerUrl={bannerImageUrl}
                  onChange={setBannerImageUrl}
                  disabled={isSubmitting}
                  uploadFolder={`projects/${communityId ?? 'unknown'}/banners`}
                />
                <ProjectDocumentsEditor
                  documents={documents}
                  onChange={setDocuments}
                  disabled={isSubmitting}
                  uploadFolder={`projects/${communityId ?? 'unknown'}/documents`}
                />
                <ContactsEditor
                  contacts={contacts}
                  onChange={setContacts}
                  disabled={isSubmitting}
                />
                <ItemsEditor items={items} onChange={setItems} disabled={isSubmitting} />
              </div>

              <div className="flex flex-col-reverse md:flex-row justify-end gap-3 pt-6 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="w-full md:w-auto text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || (isOpenForVendors && validItemCount === 0)}
                  className="w-full md:w-auto bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 px-8"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
